import {Decimal} from 'decimal.js';
import {match, Pattern} from 'ts-pattern';

import {identity, sortBy, uniq} from 'ramda';

import type {
  FeatureResponseBody,
  SelectionConditionInput,
  TrimFeatureAssignmentInput,
} from '@dms/api/new-vehicle';

import type {PricedItemChange, PricedItemChangeOperation} from '../types/PricedItemChange';

const NAME_SEPARATOR = ' · ';
const ZERO_PRICE = '0.00';
const PRICE_RANK: Record<string, number> = {
  trimFeature: 0,
  conditionalPrice: 1,
  variantPriceOverride: 2,
};

type AssignmentShape = Pick<
  TrimFeatureAssignmentInput,
  'featureId' | 'inclusion' | 'priceIf' | 'variantPriceOverrides' | 'priceNet'
>;
type LivePrice = {
  key: string;
  featureId: string;
  resourceType: 'trimFeature' | 'conditionalPrice' | 'variantPriceOverride';
  name: string;
  inclusion: AssignmentShape['inclusion'];
  chargedAsOptional: string;
};
type TrimFeatureChangesOptions = {
  trimId: string;
  trimName: string;
  /** The assignments as the server holds them (a fresh GET of the trim's feature assignments). */
  stored: AssignmentShape[];
  /** The assignments the PUT sends. */
  next: AssignmentShape[];
  /** Library items, for their names. */
  features: Pick<FeatureResponseBody, 'id' | 'name'>[];
  /** The Variant name of an override row. */
  variantName: (variantId: string) => string;
  /** The Variant in a condition; defaults to `variantName`. */
  variantOrderCode?: (variantId: string) => string;
};

const money = (value: string | null | undefined) => new Decimal(value || 0).toFixed(2);
const sortedUnique = (ids: string[]) => sortBy(identity, uniq(ids));
/** Sorted, de-duplicated sets, as the server's condition key (TrimFeaturePrice::conditionKey). */
const canonical = (condition: SelectionConditionInput): SelectionConditionInput => ({
  anyOfVariantIds: sortedUnique(condition.anyOfVariantIds),
  allOfFeatureIds: sortedUnique(condition.allOfFeatureIds),
});

/** The trim price, the first conditional price per condition and the first override per Variant (D80). */
const livePrices = (
  options: TrimFeatureChangesOptions,
  assignment: AssignmentShape
): LivePrice[] => {
  const featureNameOf = (featureId: string) =>
    options.features.find((feature) => feature.id === featureId)?.name ?? featureId;
  const featureName = featureNameOf(assignment.featureId);
  const orderCodeOf = options.variantOrderCode ?? options.variantName;
  const prefix = `${options.trimId}:${assignment.featureId}`;
  const live = (
    price: Pick<LivePrice, 'key' | 'resourceType' | 'name' | 'chargedAsOptional'>
  ): LivePrice => ({...price, featureId: assignment.featureId, inclusion: assignment.inclusion});
  const prices = new Map<string, LivePrice>();
  prices.set(
    `${prefix}:price`,
    live({
      key: `${prefix}:price`,
      resourceType: 'trimFeature',
      name: [options.trimName, featureName].join(NAME_SEPARATOR),
      chargedAsOptional: money(assignment.priceNet),
    })
  );
  assignment.priceIf.forEach((price) => {
    const condition = canonical(price.condition);
    const key = `${prefix}:if:${condition.anyOfVariantIds.join(',')}/${condition.allOfFeatureIds.join(',')}`;
    if (prices.has(key)) {
      return;
    }
    const conditionText = [
      ...condition.anyOfVariantIds.map(orderCodeOf),
      ...condition.allOfFeatureIds.map(featureNameOf),
    ].join(', ');
    prices.set(
      key,
      live({
        key,
        resourceType: 'conditionalPrice',
        name: [options.trimName, featureName, conditionText].filter(Boolean).join(NAME_SEPARATOR),
        chargedAsOptional: money(price.priceNet),
      })
    );
  });
  assignment.variantPriceOverrides.forEach((override) => {
    const key = `${prefix}:variant:${override.variantId}`;
    if (prices.has(key)) {
      return;
    }
    prices.set(
      key,
      live({
        key,
        resourceType: 'variantPriceOverride',
        name: [featureName, options.variantName(override.variantId)].join(NAME_SEPARATOR),
        chargedAsOptional: money(override.priceNet),
      })
    );
  });
  return [...prices.values()];
};

const charged = (price: LivePrice) =>
  price.inclusion === 'STANDARD' ? ZERO_PRICE : price.chargedAsOptional;

const row = (
  price: LivePrice,
  operation: PricedItemChangeOperation,
  before: string | null,
  after: string | null
) => ({
  featureId: price.featureId,
  change: {
    key: price.key,
    resourceType: price.resourceType,
    name: price.name,
    operation,
    before,
    after,
  } satisfies PricedItemChange,
});

/**
 * The prices a trim feature assignment PUT creates, removes or switches between Optional and Standard, at the price
 * the customer pays: 0.00 while Standard, else the assignment's price on the trim, or the own price of a conditional
 * price or an override (D102). A changed value of a kept price is no row (value changes go through a price
 * adjustment). Rows are ordered by feature id, then the trim price, conditional prices, overrides.
 */
export const trimFeatureChanges = (options: TrimFeatureChangesOptions): PricedItemChange[] => {
  const byKey = (assignments: AssignmentShape[]) =>
    new Map(
      assignments
        .flatMap((assignment) => livePrices(options, assignment))
        .map((price) => [price.key, price])
    );
  const stored = byKey(options.stored);
  const next = byKey(options.next);
  const removed = [...stored.values()]
    .filter((price) => !next.has(price.key))
    .map((price) => row(price, 'removed', charged(price), null));
  const createdOrSwitched = [...next.values()].flatMap((price) => {
    const before = stored.get(price.key);
    return match({
      before,
      inclusion: price.inclusion,
      isSwitched: before?.inclusion !== price.inclusion,
    })
      .with({before: Pattern.nullish}, () => [row(price, 'created', null, charged(price))])
      .with({isSwitched: false}, () => [])
      .with({before: Pattern.nonNullable, inclusion: 'STANDARD'}, ({before: storedPrice}) => [
        row(price, 'madeStandard', storedPrice.chargedAsOptional, ZERO_PRICE),
      ])
      .with({before: Pattern.nonNullable, inclusion: 'OPTIONAL'}, () => [
        row(price, 'madeOptional', ZERO_PRICE, price.chargedAsOptional),
      ])
      .exhaustive();
  });
  return sortBy(
    ({featureId, change}) => `${featureId}|${PRICE_RANK[change.resourceType]}|${change.key}`,
    [...removed, ...createdOrSwitched]
  ).map(({change}) => change);
};
